<template>
  <div class="container">
    <ValidationObserver
      ref="observer"
      v-slot="{ valid, dirty }"
    >
      <v-row>
        <VTextField
          ref="alert_name"
          v-model="formData.alertName"
          :label="$t('forms.labels.name')"
          required
          outlined
          dataCy="nameAlert"
          @input="(val)=>updateData(val,'alertName')"
          @keydown.enter="toggleNameEditMode"
        />
      </v-row>
      <v-row>
        <VSelect
          ref="alert_type"
          v-model="formData.alType"
          itemValue="key"
          required
          :label="$t('forms.labels.alertType')"
          :items="alTypes"
          outlined
          :readonly="readOnly"
          :validateAtInitialization="isEdition"
          :datacy="'alertType'"
          @input="onAlTypeSelection"
        />
      </v-row>
      <SlotScopeWatcher
        :value="dirty"
        @change="$emit('dirtyChanged', dirty)"
      />
      <SlotScopeWatcher
        :value="valid"
        @change="$emit('validChanged', valid)"
      />
    </ValidationObserver>
    <v-row>
      <div class="form-label-title">
        <h3>{{ alertLabel }}</h3>
      </div>
    </v-row>
    <v-row v-if="alertTypeIsAnalysis">
      <v-progress-linear
        v-if="loading"
        indeterminate
      />
      <div
        v-else
        class="tree-container w-100"
      >
        <Tree
          :id="mutableSite.id"
          :active="mutableSite.active"
          :nodeItem="mutableSite"
          :name="mutableSite.name"
          nodeType="Site"
          viewFrom="Alert"
          :analysesTableHeads="analysesTableHeads"
          :site="mutableSite"
          :showAll="showAll"
          :nodes="mutableSite.children"
          :analysisNormsVals="formData.alNorms"
          :readOnly="readOnly"
          @analysisNormsChange="onAnalysisNormsChange"
          @getIsAnalysisAdded="getIsAnalysisAdded"
          @nodeItemChanged="nodeItemChanged"
        />
      </div>
    </v-row>
    <v-row v-else-if="alertTypeIsInventory">
      <AlertInventoryList
        v-if="inventoryProducts.length"
        :inventoryVals="formData.alProducts"
        :inventoryProductList="inventoryProducts"
        :readOnly="readOnly"
        @inventoryProductsChange="onInventoryProductsChange"
      />
    </v-row>
    <v-row>
      <div class="form-label-title">
        <h3>{{ $t('forms.labels.alertedPeople') }}</h3>
      </div>
    </v-row>
    <v-row>
      <AlertedUserSelection
        v-if="siteUsers.length"
        :usersList="siteUsers"
        :alertedPeopleList="formData.alPeople"
        :readOnly="readOnly"
        @onAlertedUsersSelectionChange="onAlertedUsersSelection"
      />
    </v-row>
    <v-row>
      <div class="form-label-title">
        <h3>{{ $t('forms.labels.alertMessage') }}</h3>
      </div>
    </v-row>
    <v-row>
      <p
        class="alert-message-body"
      >
        {{ resultMessage }}
      </p>
    </v-row>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import { ValidationObserver } from 'vee-validate';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect';
import AlertedUserSelection from '@/components/sites/AlertedUserSelection';
import AlertInventoryList from '@/components/sites/AlertInventoryList';
import nodeMixim from '@/mixins/NodeMixim';
import roles from '@/constants/roles';

export default {
    name: 'AlertForm',
    components: {
        ValidationObserver,
        VTextField,
        SlotScopeWatcher,
        VSelect,
        AlertInventoryList,
        AlertedUserSelection,
    },
    mixins: [
        nodeMixim,
    ],
    props: {
        site: {
            type: Object,
            default: () => ({}),
        },
        alertData: {
            type: Object,
            default: () => {},
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            analysisTypeKey: 'measuresAndSamplesCollect',
            inventoryTypeKey: 'inventory',
            alTypes: [],
            userEnum: ['alTypeUser'],
            analysesTableHeads: [
                { label: 'Very Low', action: 'veryLow', color: '#AD1C3F' },
                { label: 'Low', action: 'low', color: '#D1A918' },
                { label: 'Min/Max', action: 'minMax', color: '#2A750F' },
                { label: 'High', action: 'high', color: '#D1A918' },
                { label: 'Very High', action: 'veryHigh', color: '#AD1C3F' },
            ],
            formData: {
                alType: { key: '', name: '' },
                alertName: this.$t('alerts.labels.newalert'),
                alUser: { name: 'Add Alerted User', key: 'alTypeUser' },
                alPeople: { initValue: [] },
                alNorms: [],
                alProducts: { initValue: [] },
            },
            siteUsers: [],
            inventoryProducts: [],
            nameEditMode: false,
            loading: true,
            showAll: false,
            mutableSite: {},
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'hasRole',
        ]),
        users() {
            return [{ name: 'Add Alerted User', key: 'alTypeUser' }];
        },
        alertLabel() {
            const currAlType = this.formData.alType.key ? this.formData.alType.key : this.formData.alType;
            let formLabel = '';
            if (currAlType === this.analysisTypeKey) {
                formLabel = this.$t('forms.labels.analysisAlert');
            } else if (currAlType === this.inventoryTypeKey) {
                formLabel = this.$t('forms.labels.inventoryAlert');
            }
            return formLabel;
        },
        resultMessage() {
            const currAlType = this.formData.alType.key ? this.formData.alType.key : this.formData.alType;
            let formLabel = '';
            if (currAlType === this.analysisTypeKey) {
                formLabel = this.$t('forms.labels.alMessage');
            } else if (currAlType === this.inventoryTypeKey) {
                formLabel = this.$t('forms.labels.alMessageInventory');
            }
            return formLabel;
        },
        alertTypeIsAnalysis() {
            const currAlType = this.formData.alType.key ? this.formData.alType.key : this.formData.alType;
            return (currAlType === this.analysisTypeKey);
        },
        alertTypeIsInventory() {
            const currAlType = this.formData.alType.key ? this.formData.alType.key : this.formData.alType;
            return (currAlType === this.inventoryTypeKey);
        },
        isReadOnly() {
            return this.hasRole(roles.SALES_REPRESENTATIVE.value);
        },
    },
    created() {
        this.mutableSite = cloneDeep(this.site);
        this.getData();
        if (this.isEdition) {
            this.formData.alType = { name: this.alertData.activity.label, key: this.alertData.activity.type };
            this.formData.alertName = this.alertData.name;
            this.formData.alNorms = cloneDeep(this.alertData.analysisTypes);
            this.formData.alProducts.initValue = this.alertData.productInventories;
            this.formData.alPeople.initValue = this.alertData.alertedPeople;
        }
    },
    methods: {
        async getData() {
            this.loading = true;
            if (this.mutableSite.id) {
                this.mutableSite.children = await this.getSiteTree();
                await this.getAlertTypes();
                await this.getSiteInventoryProducts();
                await this.getAvailableSiteUsers();
            }
            this.loading = false;
        },
        async getSiteTree() {
            const { data } = await this.$rq.getNodeTree(this.mutableSite.id, 'site', {
                includeinactive: false,
            });
            return data.tree;
        },
        async getAlertTypes() {
            const { data } = await this.$rq.getAlertTypes();
            this.alTypes = Object.values(data).map(type => {
                const typeObj = {
                    name: type.label,
                    key: type.type,
                };
                return typeObj;
            });
        },
        async getSiteInventoryProducts() {
            const { data } = await this.$rq.getSiteInventoryProducts({ siteId: this.mutableSite.id });
            this.inventoryProducts = data.productInventories;
        },
        async getAvailableSiteUsers() {
            const { data } = await this.$rq.getAvailableSiteUsers({ siteId: this.mutableSite.id });
            this.siteUsers = data.allUsers;
        },
        toggleNameEditMode() {
            this.nameEditMode = !this.nameEditMode;
        },
        updateData(val, formKey) {
            const payload = cloneDeep(this.formData);
            payload[formKey] = val;
            const obj = cloneDeep(this.formData);
            if (formKey === 'alType' || formKey === 'alertName' || formKey === 'alNorms') {
                obj[formKey] = val;
            } else {
                obj[formKey] = { ...val, initValue: obj[formKey].initValue };
            }
            this.formData = obj;
            this.$emit('sendData', payload);
        },
        onAlTypeSelection(val) {
            this.updateData(val, 'alType');
        },
        onAlertedUsersSelection(selectionObj) {
            this.updateData(selectionObj, 'alPeople');
        },
        onAnalysisNormsChange(normsObj) {
            this.updateData(normsObj, 'alNorms');
        },
        getIsAnalysisAdded(id) {
            const [added] = this.formData.alNorms.filter(norm => norm.id === id);
            return added;
        },
        onInventoryProductsChange(inventoryObj) {
            this.updateData(inventoryObj, 'alProducts');
        },
        nodeItemChanged(value) {
            const children = this.replaceNode(this.mutableSite, value);
            this.mutableSite = { ...this.mutableSite, children, showChildren: true };
        },
    },
};
</script>
<style scoped>
    .container{
        padding: 15px;
    }
    .alert-name-cont {
        display: flex;
    }
    .form-label-title{
        margin: 15px 0px;
    }
    .form-label-title h3{
        font-size: 18px;
        color: #26384B;
        padding-right: 5px;;
    }
    .alert-message-body{
        width: 100%;
        border: 1px solid black;
        padding: 15px;
    }
    .inventory-container{
      margin-left: 12px;
      margin-right: 12px;
    }
    .user-selection-container{
      margin-left: 12px;
      margin-right: 12px;
    }
</style>
